import classNames from "classnames";
import { FC, PropsWithChildren, ReactElement, useEffect } from "react";
import { useTabs } from "../../context/tabsContext";
import Panel from "./Panel";
import styles from "./Tabs.module.css";

interface TabsProps {
  tabs: {
    title: string;
    disabled?: boolean;
    hasError?: boolean;
  }[];
  isDialog?: boolean;
}

const Tabs: FC<TabsProps> = ({ tabs, isDialog = false }: TabsProps) => {
  const controller = useTabs();
  const selected = Math.min(controller.selectedIndex, tabs.length - 1);

  useEffect(() => {
    controller.selectedIndex > selected && controller.goToTab(selected);
  }, [controller.selectedIndex]);

  return (
    <>
      <ul
        className={classNames(styles.list, {
          [styles.listDialog]: isDialog,
        })}
      >
        {tabs.map((tab, tabIndex) => (
          <Tab
            key={`tab_${tabIndex}`}
            selected={selected == tabIndex}
            error={tab.hasError}
            onClick={() => controller.goToTab(tabIndex)}
            disabled={tab.disabled}
          >
            {tab.title}
          </Tab>
        ))}
      </ul>
    </>
  );
};

export default Tabs;

export type TabProps = PropsWithChildren<{
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  error?: boolean;
}>;

const Tab: FC<TabProps> = (props: TabProps) => {
  const { children, disabled, selected, onClick, error } = {
    ...props,
  };

  return (
    <li
      className={classNames(styles.tab, {
        [styles.tabActive]: selected,
        [styles.tabDisabled]: disabled,
        [styles.tabError]: error,
      })}
      onClick={() => !disabled && onClick()}
    >
      {children}
    </li>
  );
};

export type TabContent = {
  body: ReactElement;
  eager?: boolean;
  withBorder?: boolean;
};

type TabsContentsProps = {
  contents: TabContent[];
  noPaddingTop?: boolean;
  noPaddingHorizontal?: boolean;
  isDragging?: boolean;
};

export const TabsContents: FC<TabsContentsProps> = ({ contents, noPaddingTop, noPaddingHorizontal, isDragging }) => {
  const controller = useTabs();

  return (
    <>
      {contents.map((tc, i) =>
        tc.eager || i === controller.selectedIndex ? (
          <Panel
            withBorder={!!tc.withBorder}
            visible={i === controller.selectedIndex}
            key={`panel_${i}`}
            padding={{
              top: !noPaddingTop,
              bottom: true,
              left: !noPaddingHorizontal,
              right: !noPaddingHorizontal,
            }}
            isDragging={isDragging}
          >
            {tc.body}
          </Panel>
        ) : (
          <div key={`panel_${i}`}></div>
        ),
      )}
    </>
  );
};
